/**
 * `/products` and `/customers` — ADR-0016's two master lists, one screen parameterised by
 * `JobMasterKind`. J33…J36 and J37…J40, `12-api-contract.md`.
 *
 * ── WHY ONE COMPONENT AND TWO ROUTES ────────────────────────────────────────
 * The lists are identical in shape (`JobMasterDto` either way) and in rules, so two screens
 * would be one screen copied and left to drift. The routes stay separate because they are
 * two sider items (`nav-model.ts`) with two URLs a clerk bookmarks; each route file owns its
 * guard, search parsing and loader, and hands this component the parsed search and a setter —
 * a component cannot call `Route.useSearch()` for a route it does not know.
 *
 * ── RETIRE IS THE ORDINARY ANSWER, DELETE THE EXCEPTION ─────────────────────
 * A product or customer that any job line names, in any period, cannot be deleted (J36 / J40
 * refuse with `E_FK_VIOLATION`): deleting it would rewrite what a paid line says it was.
 * Retiring takes it out of the sheet's picker and leaves every line that carries it printing
 * it. So Delete is offered only at `lineCount === 0` — a row created by mistake — and
 * Retire / Reactivate are the everyday controls. `lineCount` sits in the table beside them,
 * the same "show the number the refusal is about" argument `agents.tsx` makes for
 * `workerCount`.
 *
 * ── A DUPLICATE NAME IS USUALLY A RETIRED ROW ───────────────────────────────
 * Names are unique per company CASE-INSENSITIVELY and retired rows included, so the 409 a
 * clerk is most likely to meet is "you retired this last year". The API answers with
 * `meta.existingId` and `meta.existingIsActive`; when the clash is retired the drawer offers
 * to reactivate it in place rather than leaving the clerk to find it under Show retired.
 *
 * ── PERMISSIONS ─────────────────────────────────────────────────────────────
 * `job.read` gates the route; `job.write` gates every control that changes the list, and
 * those are HIDDEN, not disabled — the same call `agents.tsx` makes and for the same reason
 * (§18.0.1's shown-and-disabled rule is written for the sider).
 */
import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Drawer, Input, Popconfirm, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { JobMasterDto, JobMasterKind, UpdateJobMasterRequest } from '@cropmate/shared';
import { createJobMasterRequestSchema } from '@cropmate/shared';
import { ApiError } from '@/api/client';
import { qk } from '@/api/queries';
import { useAuth } from '@/lib/auth';
import { statics } from '@/lib/antd-statics';
import { formatDate } from '@/lib/dates';
import { PAGER_AT_BOTH_ENDS } from '@/lib/pager';
import { LIST_SCROLL_Y, pinAntdColumns, useFrozenColumns, type FreezableColumn } from '@/lib/table-freeze';
import { FreezeColumnsControl, FullViewScope, PageBody, PageHeader, StatusPill, useFullView } from '@/components/primitives';
import { QueryBoundary } from '@/components/shell/QueryBoundary';
import { JOB_MASTER_COPY, invalidateJobMaster, jobMasterApi } from './job-master-api';

export const JOB_MASTER_PAGE_SIZE = 25;

/**
 * A TYPE ALIAS, not an interface: `qk.jobMaster.list` takes `Record<string, QueryValue>`, and
 * TypeScript gives an alias an implicit index signature but an interface none. (The same note
 * `AgentsSearch` carries.)
 */
export type JobMasterSearch = {
  q?: string;
  page?: number;
  includeInactive?: boolean;
};

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined;
}

function bool(v: unknown): boolean | undefined {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return undefined;
}

/** Shared by both route files' `validateSearch`. */
export function validateJobMasterSearch(search: Record<string, unknown>): JobMasterSearch {
  const page = Number(search['page']);
  return {
    q: str(search['q']),
    page: Number.isFinite(page) && page > 1 ? Math.floor(page) : undefined,
    includeInactive: bool(search['includeInactive']),
  };
}

/** The J33 / J37 query for a parsed search — one function so the loader and the screen can
 *  never ask for different pages of the same list. */
export function jobMasterListQuery(kind: JobMasterKind, search: JobMasterSearch) {
  return qk.jobMaster.list(kind, {
    page: search.page ?? 1,
    pageSize: JOB_MASTER_PAGE_SIZE,
    ...(search.q === undefined ? {} : { q: search.q }),
    ...(search.includeInactive ? { includeInactive: true } : {}),
  });
}

/* ══════════════════════════════ the drawer ══════════════════════════════ */

/**
 * One flat state object, as on Agents: two trimmed strings and no cross-field rule. `''` is
 * the empty note in the form and `null` on the wire — `optionalText` rejects `''` outright.
 */
interface FormState {
  /** `null` while creating; the row's id while editing. */
  id: string | null;
  name: string;
  note: string;
}

const EMPTY: FormState = { id: null, name: '', note: '' };

const blank = (s: string): string | null => (s.trim() === '' ? null : s.trim());

/** A 409 duplicate whose clash is a RETIRED row — the case worth a button. */
interface RetiredClash {
  id: string;
  message: string;
}

function retiredClashOf(error: unknown): RetiredClash | null {
  if (!(error instanceof ApiError) || !error.is('E_CONFLICT_DUPLICATE')) return null;
  const id = error.metaValue<string>('existingId');
  const active = error.metaValue<boolean>('existingIsActive');
  return typeof id === 'string' && active === false ? { id, message: error.message } : null;
}

/* ══════════════════════════════ the screen ══════════════════════════════ */

export interface JobMasterScreenProps {
  kind: JobMasterKind;
  search: JobMasterSearch;
  /** Any filter change; the route resets to page 1. */
  onSearch: (patch: Partial<JobMasterSearch>) => void;
  onPage: (page: number) => void;
}

/** The columns a user may freeze on Products and Customers (`lib/table-freeze.ts`). */
const JOB_MASTER_FREEZABLE: readonly FreezableColumn[] = [
  { key: 'name', title: 'Name' },
  { key: 'note', title: 'Note' },
  { key: 'lines', title: 'Lines' },
  { key: 'lastUsed', title: 'Last used' },
  { key: 'status', title: 'Status' },
];

export function JobMasterScreen({ kind, search, onSearch, onPage }: JobMasterScreenProps) {
  const copy = JOB_MASTER_COPY[kind];
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const mayWrite = can('job.write');

  const [form, setForm] = useState<FormState | null>(null);
  const [draftQuery, setDraftQuery] = useState(search.q ?? '');

  const list = useQuery({
    ...jobMasterListQuery(kind, search),
    /* A VALUE, not `keepPreviousData: true` (§4.6) — dim the rows, don't collapse them. */
    placeholderData: keepPreviousData,
  });
  const rows = list.data?.items ?? [];
  const total = list.data?.page.total ?? 0;

  const save = useMutation({
    mutationFn: (state: FormState) =>
      state.id === null
        ? jobMasterApi.create(kind, { name: state.name.trim(), note: blank(state.note) })
        : jobMasterApi.update(kind, state.id, { name: state.name.trim(), note: blank(state.note) }),
    onSuccess: async (row, state) => {
      await invalidateJobMaster(queryClient, kind);
      statics.message?.success(state.id === null ? `${row.name} added.` : `${row.name} updated.`);
      setForm(null);
    },
    /* 409 is not toasted by the MutationCache (`query-client.ts`), so a duplicate name is
     * rendered inside the drawer below or it vanishes. */
  });

  /* `useMutation` holds `error` until the next attempt, so a reopened drawer must be reset or
   * it greets the next entry with the last one's duplicate-name refusal. */
  function openForm(next: FormState | null): void {
    save.reset();
    setForm(next);
  }

  /* Retire, reactivate — and the drawer's "reactivate the retired one" — are one PATCH. */
  const setActive = useMutation({
    mutationFn: (v: { id: string; body: UpdateJobMasterRequest }) =>
      jobMasterApi.update(kind, v.id, v.body),
    onSuccess: async (row) => {
      await invalidateJobMaster(queryClient, kind);
      statics.message?.success(
        row.isActive
          ? `${row.name} is active again and suggested on the job sheet again.`
          : `${row.name} retired. Lines that already carry it still print it.`,
      );
      setForm(null);
    },
    onError: (error: unknown) => {
      /* Only a 409 needs saying here; the MutationCache has toasted everything else. A
       * reactivation can clash too — an active row renamed into this one's name meanwhile. */
      if (error instanceof ApiError && error.status === 409) {
        statics.notification?.error({ message: 'That change was refused.', description: error.message });
      }
    },
  });

  const remove = useMutation({
    mutationFn: (row: JobMasterDto) => jobMasterApi.remove(kind, row.id),
    onSuccess: async (_: unknown, row) => {
      await invalidateJobMaster(queryClient, kind);
      statics.message?.success(`${row.name} deleted.`);
    },
    onError: (error: unknown) => {
      /* The button is offered only at `lineCount === 0`, so reaching this means a clerk keyed
       * a line naming the row while this page was open. The API's sentence already says
       * "retire it instead" and names the count — render it rather than paraphrase it. */
      if (error instanceof ApiError && error.status === 409) {
        statics.notification?.error({
          message: `That ${copy.singular} is in use.`,
          description: error.message,
        });
      }
    },
  });

  /* `lib/table-freeze.ts` (client request, 14 Oct 2026) — the header always stays; ticked columns
   * move left and stay. Products and Customers remember their choices separately. */
  const [frozen, setFrozen] = useFrozenColumns(`jobMaster.${kind}`, JOB_MASTER_FREEZABLE);
  /* `FullView.tsx` — the header, filters and table fill the window. */
  const fullView = useFullView();

  const columns: ColumnsType<JobMasterDto> = [
    {
      key: 'name',
      title: 'Name',
      render: (_: unknown, r) => (
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: r.isActive ? 'var(--text-strong)' : 'var(--neutral-400)',
          }}
        >
          {r.name}
        </span>
      ),
    },
    {
      key: 'note',
      title: 'Note',
      render: (_: unknown, r) =>
        r.note === null ? (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>
        ) : (
          <span
            title={r.note}
            style={{
              display: 'block',
              maxWidth: 360,
              fontSize: 12,
              color: 'var(--text-body)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {r.note}
          </span>
        ),
    },
    {
      /* A plain number, not a link into Job Lines: J41 requires a date range of at most 366
       * days and `lineCount` counts every period, so a link would land on a search that shows
       * fewer lines than the number clicked — which reads as a bug. `Last used` beside it is
       * what tells a clerk where to look. */
      key: 'lines',
      title: 'Lines',
      width: 80,
      align: 'right',
      render: (_: unknown, r) => (
        <span
          className="cm-figure"
          style={{ fontSize: 12, color: r.lineCount === 0 ? 'var(--text-muted)' : 'var(--text-body)' }}
        >
          {r.lineCount}
        </span>
      ),
    },
    {
      key: 'lastUsed',
      title: 'Last used',
      width: 120,
      render: (_: unknown, r) => (
        <span className="cm-figure" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {formatDate(r.lastUsedOn)}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      width: 92,
      render: (_: unknown, r) =>
        r.isActive ? (
          <StatusPill kind="valid">Active</StatusPill>
        ) : (
          <StatusPill kind="closed">Retired</StatusPill>
        ),
    },
    ...(mayWrite
      ? [
          {
            key: 'actions',
            title: '',
            width: 240,
            render: (_: unknown, r: JobMasterDto) => {
              const busy = setActive.isPending && setActive.variables?.id === r.id;
              return (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    onClick={() => openForm({ id: r.id, name: r.name, note: r.note ?? '' })}
                  >
                    Edit
                  </Button>
                  {r.isActive ? (
                    <Popconfirm
                      title={`Retire this ${copy.singular}?`}
                      description={
                        <span style={{ maxWidth: 300, display: 'block' }}>
                          It stops being offered on the job sheet
                          {r.lineCount === 0
                            ? ''
                            : r.lineCount === 1
                              ? '. The 1 line that already carries it keeps it'
                              : `. The ${r.lineCount} lines that already carry it keep it`}
                          , and you can reactivate it at any time.
                        </span>
                      }
                      okText="Retire"
                      onConfirm={() => setActive.mutate({ id: r.id, body: { isActive: false } })}
                    >
                      <Button size="small" loading={busy}>
                        Retire
                      </Button>
                    </Popconfirm>
                  ) : (
                    <Button
                      size="small"
                      loading={busy}
                      onClick={() => setActive.mutate({ id: r.id, body: { isActive: true } })}
                    >
                      Reactivate
                    </Button>
                  )}
                  {r.lineCount > 0 ? (
                    /* Disabled with the reason, not absent: J36 / J40 would refuse it, and a
                     * button that only fails on click teaches nothing. */
                    <Tooltip
                      title={`${r.lineCount} job line${r.lineCount === 1 ? '' : 's'} name this ${copy.singular}. Retire it instead.`}
                    >
                      <Button size="small" danger disabled>
                        Delete
                      </Button>
                    </Tooltip>
                  ) : (
                    <Popconfirm
                      title={`Delete this ${copy.singular}?`}
                      description={
                        <span style={{ maxWidth: 300, display: 'block' }}>
                          No job line names it, so nothing loses a label. This cannot be undone.
                        </span>
                      }
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => remove.mutate(r)}
                    >
                      <Button
                        size="small"
                        danger
                        /* Scoped to THIS row, or every Delete on the page spins at once. */
                        loading={remove.isPending && remove.variables?.id === r.id}
                      >
                        Delete
                      </Button>
                    </Popconfirm>
                  )}
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const editing = form !== null && form.id !== null;
  /* The same schema the API's DTO is transcribed into, so a 161-character name is caught
   * here rather than by a 400 after a round-trip. */
  const parsed =
    form === null
      ? null
      : createJobMasterRequestSchema.safeParse({ name: form.name.trim(), note: blank(form.note) });
  const formOk = form !== null && form.name.trim() !== '' && parsed?.success === true;
  const clash = retiredClashOf(save.error);
  const serverMessage = clash === null && save.error instanceof ApiError ? save.error.message : null;

  return (
    <>
      <FullViewScope view={fullView}>
        <PageHeader
          title={copy.title}
          subtitle={copy.subtitle}
          actions={
            mayWrite ? (
              <Button type="primary" onClick={() => openForm({ ...EMPTY })}>
                <span className="cm-btn-label">Add {copy.singular}</span>
              </Button>
            ) : null
          }
        >
          <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Input.Search
              allowClear
              size="small"
              style={{ width: 260 }}
              placeholder="Search name"
              value={draftQuery}
              onChange={(e) => setDraftQuery(e.target.value)}
              onSearch={(value) => onSearch({ q: value.trim() || undefined })}
            />
            <Button
              size="small"
              type={search.includeInactive ? 'primary' : 'default'}
              onClick={() => onSearch({ includeInactive: search.includeInactive ? undefined : true })}
            >
              {search.includeInactive ? 'Showing retired' : 'Show retired'}
            </Button>
            <span style={{ marginLeft: 'auto' }}>
              <FreezeColumnsControl columns={JOB_MASTER_FREEZABLE} value={frozen} onChange={setFrozen} fullView={fullView} />
            </span>
          </div>
        </PageHeader>

        <PageBody>
          <QueryBoundary
            isPending={list.isPending}
            error={list.error}
            onRetry={() => void list.refetch()}
            skeletonRows={8}
          >
            <div
              style={{
                background: 'var(--surface-card)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 4,
                /* No `overflow-x: auto`: the table scrolls itself, both ways, so its header stays
                 * (`lib/table-freeze.ts`). */
              }}
            >
              <Table<JobMasterDto>
                rowKey="id"
                size="small"
                columns={pinAntdColumns(columns, frozen)}
                scroll={{ x: 900, y: LIST_SCROLL_Y }}
                dataSource={rows}
                loading={list.isFetching}
                locale={{
                  emptyText:
                    search.q !== undefined
                      ? `No ${copy.singular} matches “${search.q}”.`
                      : search.includeInactive
                        ? `No ${copy.singular}s yet.`
                        : `No active ${copy.singular}s. Retired ones are under Show retired.`,
                }}
                pagination={{
                  current: search.page ?? 1,
                  pageSize: JOB_MASTER_PAGE_SIZE,
                  total,
                  showSizeChanger: false,
                  placement: PAGER_AT_BOTH_ENDS,
                  hideOnSinglePage: true,
                  onChange: onPage,
                }}
              />
            </div>
          </QueryBoundary>
        </PageBody>
      </FullViewScope>

      <Drawer
        open={form !== null}
        onClose={() => openForm(null)}
        /* antd 6: `size` and `destroyOnHidden`; the old names compile and do nothing. */
        size="default"
        destroyOnHidden
        title={editing ? `Edit ${copy.singular}` : `New ${copy.singular}`}
      >
        {form !== null ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <label>
              <div className="cm-section-label">Name</div>
              <Input
                value={form.name}
                maxLength={160}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={copy.placeholder}
              />
              <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                {/* The API collapses inner whitespace and compares case-insensitively, so
                  * `cm900` and `CM 900` are the same name — say so before the 409 does. */}
                Unique ignoring case and extra spaces, retired {copy.singular}s included.
                {editing ? ' A rename shows on every line that already carries it.' : ''}
              </div>
            </label>

            <label>
              <div className="cm-section-label">Note (optional)</div>
              <Input.TextArea
                value={form.note}
                maxLength={2000}
                rows={3}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </label>

            {clash !== null ? (
              <div
                role="alert"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  fontSize: 12,
                  color: 'var(--text-body)',
                }}
              >
                {/* The server's sentence already says the row is retired and to reactivate it
                  * (`JobMasterService.assertNameFree`), so it is shown alone — a second sentence
                  * of our own said the same thing twice (guide review, 2026-10-01). */}
                <span>{clash.message}</span>
                <div>
                  <Button
                    size="small"
                    loading={setActive.isPending}
                    onClick={() => setActive.mutate({ id: clash.id, body: { isActive: true } })}
                  >
                    Reactivate the retired {copy.singular}
                  </Button>
                </div>
              </div>
            ) : null}

            {serverMessage ? (
              <div role="alert" style={{ fontSize: 12, color: 'var(--status-danger)' }}>
                {serverMessage}
              </div>
            ) : null}

            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                type="primary"
                loading={save.isPending}
                disabled={!formOk || save.isPending}
                onClick={() => save.mutate(form)}
              >
                {editing ? 'Save changes' : `Add ${copy.singular}`}
              </Button>
              <Button onClick={() => openForm(null)} disabled={save.isPending}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </Drawer>
    </>
  );
}
